import { apiProductAnnouncements } from "@/api/product-announcement.api.ts";
import ProgressiveBlurCircle from "@/components/decorations/ProgressiveBlurCircle.tsx";
import {
  productAnnouncementsKeys,
  useCurrentProductAnnouncement,
} from "@/queries/use-product-announcements.query.ts";
import { ApiProductAnnouncementEventType } from "@/types/product-announcement.types.ts";
import { Box, ButtonIcon, FontAwesomeIcon, Stack, Text, Title } from "@skailer-com/uikit";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export const HomeAnnouncement = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: announcement } = useCurrentProductAnnouncement();
  const announcementId = announcement?.id;

  useEffect(() => {
    if (announcementId === undefined) return;
    void apiProductAnnouncements.registerEvent(announcementId, "viewed");
  }, [announcementId]);

  if (!announcement) return null;

  const dismiss = (eventType: ApiProductAnnouncementEventType) => {
    queryClient.setQueryData(productAnnouncementsKeys.current.queryKey, null);
    void apiProductAnnouncements.registerEvent(announcement.id, eventType);
  };

  const handleClick = () => {
    if (/^https?:\/\//i.test(announcement.link_url)) {
      window.open(announcement.link_url, "_blank", "noopener,noreferrer");
    }
    dismiss("clicked");
  };

  return (
    <>
      <Box onClick={handleClick} className="w-full text-left cursor-pointer">
        <Stack direction="horizontal" justify="between" items="center">
          <Title
            header={t("core:message_from_app")}
            size={"lg"}
            paddingBottom
            paddingTop
            actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
          />
          <ButtonIcon
            icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
            variant="secondary"
            aria-label={t("common:close")}
            onClick={(event) => {
              event.stopPropagation();
              dismiss("closed");
            }}
          />
        </Stack>
        <Stack
          className={"bg-dark/5 rounded-sm px-4.5 pt-4.5 w-full flex-1 overflow-hidden relative"}
          justify={"between"}
          direction="horizontal"
        >
          <Box className={"absolute -top-[45px] right-[135px] "}>
            <ProgressiveBlurCircle
              radius={122}
              strokeWidth={21}
              startBlur={10}
              endBlur={54}
              startColor="#FD5151"
              endColor="#F5AC3E"
              angleBlur={0}
              angleGradient={108}
            />
          </Box>

          <Box className={"absolute top-[38px] right-[9px] "}>
            <ProgressiveBlurCircle
              radius={152}
              strokeWidth={21}
              startBlur={10}
              endBlur={54}
              startColor="#2BC7A0"
              endColor="#82C82D"
              angleBlur={0}
              angleGradient={108}
            />
          </Box>

          <Stack gap="sm" className="flex-1" justify={"between"}>
            <Title
              header={announcement.title}
              size={"xs"}
              headerIcon={
                <FontAwesomeIcon icon={"fa-solid fa-circle-dot"} className={"text-warning"} />
              }
            />
            <Stack direction="horizontal" gap="sm" items="center" className={"pb-4.5"}>
              <Text className="text-lg tracking-wide">
                {announcement.text} 
                <FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />
              </Text>
            </Stack>
          </Stack>

          <div className="hidden sm:block w-[250px] h-[166px] shrink-0 rounded-sm shadow-md overflow-hidden -mb-2 relative">
            <img src={announcement.image_url} alt="" className="w-full h-full object-cover" />
          </div>
        </Stack>
      </Box>
    </>
  );
};
